/**
 * Hook for menu scope mode identification
 * Provides current scope mode identification for menu maker containers
 *
 * @module hooks/menuScope/useMenuScopeIdentifier
 */
import { MENU_SCOPE } from 'utils/menuMaker/constants';

import { useMenuScopeManagement } from './useMenuScopeManagement';
import { useMenuScopeUserState } from './useMenuScopeUserState';

/**
 * Interface for menu scope mode identification
 *
 * @interface MenuScopeIdentifierReturn
 */
interface MenuScopeIdentifierReturn {
  /** Whether current scope is STORE (branch level) with restrictions */
  isStoreMode: boolean;
  /** Whether user has full access (organization mode OR existing user in store mode) */
  isOrganizationMode: boolean;
  /** Whether current scope is ORGANIZATION (for titles and labels) */
  isOrganizationScope: boolean;
}

/**
 * Hook for identifying current menu scope mode
 * Provides boolean flags to determine if user is in store or organization mode
 *
 * Store mode is only considered "restrictive" for new users (mainMenuOrganization = true).
 * Existing users (mainMenuOrganization = false) get full access even in store scope.
 *
 * @returns {MenuScopeIdentifierReturn} Mode identification flags
 * @returns {boolean} returns.isStoreMode - Whether current scope is STORE AND user should have restrictions
 * @returns {boolean} returns.isOrganizationMode - Whether user has full access (organization mode OR existing user in store mode)
 * @returns {boolean} returns.isOrganizationScope - Whether current scope is ORGANIZATION (for titles and labels)
 *
 * @example
 * const { isStoreMode, isOrganizationMode, isOrganizationScope } = useMenuScopeIdentifier();
 *
 * if (isStoreMode) {
 *   // Enable store-specific functionality with restrictions (only for new users)
 *   enableKitchenAreaAssignment();
 * }
 *
 * if (isOrganizationMode) {
 *   // Enable full functionality (organization or existing user in store mode)
 *   enableMenuCreation();
 * }
 *
 * // For titles and labels
 * const { titleKey } = getScopeBasedTranslationKeys(isOrganizationScope, 'resume');
 */
export const useMenuScopeIdentifier = (): MenuScopeIdentifierReturn => {
  const { selectedScope } = useMenuScopeManagement();
  const { user } = useMenuScopeUserState();

  // Store mode is only "restrictive" if user is new (mainMenuOrganization = true)
  // Existing users (mainMenuOrganization = false) get full access even in store scope
  const isStoreMode = selectedScope === MENU_SCOPE.STORE && (user?.mainMenuOrganization ?? false);
  const isOrganizationMode =
    selectedScope === MENU_SCOPE.ORGANIZATION ||
    (selectedScope === MENU_SCOPE.STORE && !(user?.mainMenuOrganization ?? false));

  // For titles and labels - based solely on the actual selected scope
  const isOrganizationScope = selectedScope === MENU_SCOPE.ORGANIZATION;

  return {
    isStoreMode,
    isOrganizationMode,
    isOrganizationScope,
  };
};
